import * as React from 'react';

import { fromJson } from '@bufbuild/protobuf';
import {
  AssetEvent,
  AssetEventSchema,
} from '@rematter/frontend-lib-api-client/grpc/transportation/v1/asset_events_pb';
import { Col, EventDetailContainer, JsonViewer } from '@rematter/ui-web';

import { AssetCreatedDetail } from './AssetCreatedDetail';
import { AssetDeletedDetail } from './AssetDeletedDetail';
import { AssetUpdatedDetail } from './AssetUpdatedDetail';

type AssetEventDetailProps = {
  payload: string;
};

export const AssetEventDetail = ({ payload }: AssetEventDetailProps) => {
  const [showJson, setShowJson] = React.useState(false);

  const event = React.useMemo((): AssetEvent | null => {
    try {
      return fromJson(AssetEventSchema, JSON.parse(payload));
    } catch {
      return null;
    }
  }, [payload]);

  if (!event) {
    return <JsonViewer data={payload} title="Raw Payload (Parse Error)" />;
  }

  const { event: inner } = event;

  const renderEventContent = () => {
    switch (inner.case) {
      case 'created':
        return <AssetCreatedDetail event={inner.value} />;
      case 'updated':
        return <AssetUpdatedDetail event={inner.value} />;
      case 'deleted':
        return <AssetDeletedDetail event={inner.value} />;
      case 'facilityUpdated':
      case 'locationUpdated':
        return <JsonViewer data={payload} title="Event Data" />;
      case undefined:
        return <JsonViewer data={payload} title="Unknown Event Type" />;
    }
  };

  const getEventTitle = (): string => {
    switch (inner.case) {
      case 'created':
        return 'Asset Created';
      case 'updated':
        return 'Asset Updated';
      case 'deleted':
        return 'Asset Deleted';
      case 'facilityUpdated':
        return 'Asset Facility Updated';
      case 'locationUpdated':
        return 'Asset Location Updated';
      case undefined:
      default:
        return 'Unknown Event';
    }
  };

  return (
    <Col className="@AssetEventDetail flex-1">
      <EventDetailContainer
        title={getEventTitle()}
        showJson={showJson}
        onToggleJson={() => setShowJson(!showJson)}
        jsonContent={payload}
      >
        {renderEventContent()}
      </EventDetailContainer>
    </Col>
  );
};
